import type React from 'react'
import styles from './PromoBanner.module.css'
import { StarGlyph } from '../icons/SdGlyphs'
import moonSunUrl from '../assets/moon-sun.png'

export interface PromoBannerProps {
  title: string
  description: string
  /** Pill label ("Get Premium", "Get the app"). */
  cta: string
  /** Renders the banner as a link to `href`; otherwise as a button. */
  href?: string
  onTap: () => void
  testId: string
}

/**
 * Promo banner — Figma SD/TimetableUpsellBanner (6974:29525): promo
 * gradient (left→right `gradient/promo-start` → `-end`), brand star glyph,
 * title `boldPx16` / description `regularPx14` (both text-primary), an
 * inverted pill, and the moon_sun decorative art right-anchored behind the
 * content. The timetable upsell (a button) and the line view's "get the
 * app" CTA (a store link, LineView B/C/D) share it.
 */
export function PromoBanner({
  title,
  description,
  cta,
  href,
  onTap,
  testId,
}: PromoBannerProps): React.ReactElement {
  const content = (
    <>
      <span className={styles.starIcon}>
        <StarGlyph size={28} />
      </span>
      <span className={styles.column}>
        <span className={styles.titleBlock}>
          <span className={styles.title}>{title}</span>
          <span className={styles.description}>{description}</span>
        </span>
        <span className={styles.cta}>{cta}</span>
      </span>
      {/*
        moon_sun decorative art — right-anchored behind the content. Verbatim
        Figma export (node 7001:14325, section 6967:29525), 704x532.

        Loaded as a real asset rather than the base64 data URI it used to be:
        the encoding inflated 159 KB of PNG into 212 KB of JS source that every
        visitor parsed on the main thread whether or not they ever opened
        Station Details, and it could not be cached separately from the bundle.

        The trade-off is that the art now arrives over its own request, so on a
        cold cache the banner can paint once without it. That is acceptable
        precisely because the image is decorative (`aria-hidden`, anchored
        behind the copy) — no information is missing in that frame — and
        `decoding="async"` keeps it off the critical rendering path.
      */}
      <img className={styles.moonSun} src={moonSunUrl} alt="" aria-hidden="true" decoding="async" />
    </>
  )
  if (href !== undefined) {
    return (
      <a href={href} onClick={onTap} data-testid={testId} className={styles.banner}>
        {content}
      </a>
    )
  }
  return (
    <button type="button" onClick={onTap} data-testid={testId} className={styles.banner}>
      {content}
    </button>
  )
}
